import { useCallback, useMemo, useReducer, useState } from 'react'
import { FormInstance, LabeledValue, FormNamePath } from '@commutifi-fe/ui'
import { getArray } from 'utils/helpers'
import { getInitialValue } from '../_utils'
import vehicleReducer, { VehicleStateProps } from './reducer'
import * as stateChangeTypes from './stateChangeTypes'

export { stateChangeTypes }

export type ReducerFunction = (
  state: VehicleStateProps,
  action: stateChangeTypes.VehicleActionTypes & { changes: VehicleStateProps }
) => VehicleStateProps

interface VehicleHookUserProps {
  // Use the state reducer to have a visibility on this hook actions
  // updates and be able to change the internal state of the hook
  // based on those action types (stateChangeTypes)
  stateReducer?: ReducerFunction

  // Initial values
  initialYear?: number
  initialMake?: string
  initialModel?: LabeledValue
  initialEngineType?: LabeledValue
  initialIsUnknown?: boolean

  // Pass your form instance when you split up in multiple
  // Form Items (ex. year picker is wrapped in a Form.Item with a name
  // which will register that field on antd Form)
  // This is to allow this hook to keep in sync with the form values by
  // registering each form fields and updating them when any of the values
  // changes
  form?: FormInstance
}

interface VehicleDefaultProps {
  stateReducer: ReducerFunction
}

type VehicleProps = VehicleHookUserProps & VehicleDefaultProps

type SelectYearFunc = (year: number) => void
type SelectMakeFunc = (make: string) => void
type SelectModelFunc = (model: LabeledValue) => void
type SelectEngineTypeFunc = (engineType: LabeledValue | undefined) => void
type SelectIsUnknownFunc = (value: boolean) => void
type SelectIsCustomFunc = (value: boolean) => void
type RegisterFieldFunc = (targetedField: UseVehicleFields | undefined, fieldName: FormNamePath) => void
export type FieldsRegistryMap = Map<UseVehicleFields, FormNamePath>

const defaultStateValues: VehicleStateProps = {
  year: undefined,
  make: undefined,
  model: undefined,
  engineType: undefined,
  isUnknown: false,
  isCustom: false
}

export interface UseVehicleContextValue extends VehicleStateProps {
  selectYear: SelectYearFunc
  selectMake: SelectMakeFunc
  selectModel: SelectModelFunc
  selectEngineType: SelectEngineTypeFunc
  setIsUnknown: SelectIsUnknownFunc
  setIsCustom: SelectIsCustomFunc
  resetVehicle: () => void
  registerFieldName: RegisterFieldFunc
  genericModelId?: string
  isInteractive?: boolean
}

export enum UseVehicleFields {
  Year = 'year',
  Make = 'make',
  Model = 'model',
  FuelType = 'engineType',
  IsUnknown = 'isUnknown'
}

const getInitialState = (props: VehicleProps) => {
  const year = getInitialValue(props, 'year', defaultStateValues)
  const make = getInitialValue(props, 'make', defaultStateValues)
  const model = getInitialValue(props, 'model', defaultStateValues)
  const engineType = getInitialValue(props, 'engineType', defaultStateValues)
  const isUnknown = getInitialValue(props, 'isUnknown', defaultStateValues)
  const isCustom = getInitialValue(props, 'isCustom', defaultStateValues)

  return {
    year,
    make,
    model,
    engineType,
    isUnknown,
    isCustom
  }
}

const defaultProps: { stateReducer: ReducerFunction } = {
  stateReducer: (_, action) => action.changes
}

/**
 * Custom hook to handle logic to control vehicles year, make and model selectors.
 * This hook expose a stateReducer property which allow more flexibility for future uses of this hook.
 * (See useModel for a more extension comment on this pattern)
 *
 * @param userProps - User input prop of useVehicle
 * @param   stateReducer - reducer to control internal state for customized use cases
 *
 */
const useVehicle = ({ form, ...userProps }: VehicleHookUserProps = {}): UseVehicleContextValue => {
  const props: VehicleProps = {
    ...defaultProps,
    ...userProps
  }
  const { stateReducer } = props

  // Initial state depending on controlled props + Setup fields registration callback.
  const [fieldsRegistry, setFieldsRegistry] = useState<FieldsRegistryMap>(new Map())
  const registerFieldName = useCallback(
    (targetedField: UseVehicleFields | undefined, fieldName: FormNamePath) => {
      if (targetedField && fieldName && !fieldsRegistry.get(targetedField)) {
        fieldsRegistry.set(targetedField, fieldName)
        setFieldsRegistry(new Map(fieldsRegistry))
      }
    },
    [fieldsRegistry]
  )
  const initialState: VehicleStateProps = getInitialState(props)

  // Reducer init.
  const [{ year, make, model, engineType, isUnknown, isCustom }, dispatch] = useReducer(
    (state: VehicleStateProps, action: stateChangeTypes.VehicleActionTypes) => {
      const changes = vehicleReducer(state, action)
      return stateReducer(state, { ...action, changes })
    },
    initialState
  )

  const setFormField = useCallback(
    (fieldName: FormNamePath, value: any) => {
      if (form && fieldName) {
        const name = getArray(fieldName)
        form.setFieldsValue({ [name.join('.')]: value })
      }
    },
    [form]
  )

  const selectYear: SelectYearFunc = useCallback(
    (y) => {
      dispatch({
        type: stateChangeTypes.SelectYear,
        payload: { year: y }
      })
      // NOTE: set form field is meant to update the form values, while dispatch is meant to update the state store
      setFormField(fieldsRegistry.get(UseVehicleFields.Year), y)
      setFormField(fieldsRegistry.get(UseVehicleFields.Make), undefined)
      setFormField(fieldsRegistry.get(UseVehicleFields.Model), undefined)
      setFormField(fieldsRegistry.get(UseVehicleFields.FuelType), undefined)
    },
    [fieldsRegistry, setFormField]
  )
  const selectMake: SelectMakeFunc = useCallback(
    (m) => {
      if (m === useVehicle.CUSTOM_VEHICLE_VALUE_STR) {
        dispatch({
          type: stateChangeTypes.SetIsCustom
        })
        setFormField(fieldsRegistry.get(UseVehicleFields.Make), undefined)
        setFormField(fieldsRegistry.get(UseVehicleFields.Model), undefined)
        setFormField(fieldsRegistry.get(UseVehicleFields.FuelType), undefined)
        return
      }
      dispatch({
        type: stateChangeTypes.SelectMake,
        payload: { make: m }
      })
      // Note: set form field is meant to update the form values, while dispatch is meant to update the state store
      setFormField(fieldsRegistry.get(UseVehicleFields.Make), m)
      setFormField(fieldsRegistry.get(UseVehicleFields.Model), undefined)
      setFormField(fieldsRegistry.get(UseVehicleFields.FuelType), undefined)
    },
    [fieldsRegistry, setFormField]
  )
  const selectModel: SelectModelFunc = useCallback(
    (m) => {
      setFormField(fieldsRegistry.get(UseVehicleFields.FuelType), undefined)
      if (m && m.value === useVehicle.CUSTOM_VEHICLE_VALUE_STR) {
        dispatch({
          type: stateChangeTypes.SetIsCustom
        })
        setFormField(fieldsRegistry.get(UseVehicleFields.Model), undefined)
        setFormField(fieldsRegistry.get(UseVehicleFields.FuelType), undefined)
        return
      }
      dispatch({
        type: stateChangeTypes.SelectModel,
        payload: { model: m }
      })

      // NOTE: set form field is meant to update the form values, while dispatch is meant to update the state store
      setFormField(fieldsRegistry.get(UseVehicleFields.Model), m)
    },
    [fieldsRegistry, setFormField]
  )

  const selectEngineType: SelectEngineTypeFunc = useCallback(
    (engineType) => {
      if (engineType && engineType.value === useVehicle.CUSTOM_VEHICLE_VALUE_STR) {
        dispatch({
          type: stateChangeTypes.SetIsCustom
        })

        setFormField(fieldsRegistry.get(UseVehicleFields.Model), undefined)
        setFormField(fieldsRegistry.get(UseVehicleFields.FuelType), undefined)
        return
      }
      dispatch({
        type: stateChangeTypes.SelectEngineType,
        payload: { engineType: engineType || { value: '', label: '' } }
      })

      // NOTE: set form field is meant to update the form values, while dispatch is meant to update the state store
      setFormField(fieldsRegistry.get(UseVehicleFields.FuelType), engineType)
    },
    [fieldsRegistry, setFormField]
  )

  const setIsUnknown = useCallback((unknown: boolean) => {
    dispatch({
      type: stateChangeTypes.SetIsUnknown,
      payload: { isUnknown: unknown }
    })
  }, [])

  const setIsCustom = useCallback(
    (custom: boolean | undefined) => {
      dispatch({
        type: stateChangeTypes.SetIsCustom,
        payload: { isCustom: Boolean(custom) }
      })
      if (custom === false) {
        form?.resetFields()
      }
    },
    [form]
  )

  const resetVehicle = useCallback(() => {
    dispatch({
      type: stateChangeTypes.ResetVehicle
    })
  }, [])

  // Propagate changed to the form instance if it exists
  const yearField = useMemo(() => fieldsRegistry.get(UseVehicleFields.Year), [fieldsRegistry])
  const makeField = useMemo(() => fieldsRegistry.get(UseVehicleFields.Make), [fieldsRegistry])
  const modelField = useMemo(() => fieldsRegistry.get(UseVehicleFields.Model), [fieldsRegistry])
  const engineTypeField = useMemo(() => fieldsRegistry.get(UseVehicleFields.FuelType), [fieldsRegistry])
  const currentFormYearValue = form && yearField ? form.getFieldValue(yearField) : year
  const currentFormMakeValue = form && makeField ? form.getFieldValue(makeField) : make
  const currentFormModelValue = form && modelField ? form.getFieldValue(modelField) : model
  const currentFormEngineTypeValue = form && engineTypeField ? form.getFieldValue(engineTypeField) : engineType
  const value = useMemo(
    () => ({
      // actions.
      selectYear,
      selectMake,
      selectModel,
      selectEngineType,
      setIsUnknown,
      setIsCustom,
      resetVehicle,
      registerFieldName,
      // state.
      year: currentFormYearValue,
      make: currentFormMakeValue,
      model: currentFormModelValue,
      engineType: currentFormEngineTypeValue,
      isUnknown,
      isCustom
    }),
    [
      currentFormMakeValue,
      currentFormModelValue,
      currentFormYearValue,
      currentFormEngineTypeValue,
      isUnknown,
      isCustom,
      registerFieldName,
      resetVehicle,
      selectMake,
      selectModel,
      selectEngineType,
      selectYear,
      setIsUnknown,
      setIsCustom
    ]
  )

  return value
}

const CUSTOM_VEHICLE_VALUE = 10000
useVehicle.CUSTOM_VEHICLE_VALUE_INT = CUSTOM_VEHICLE_VALUE
useVehicle.CUSTOM_VEHICLE_VALUE_STR = `${CUSTOM_VEHICLE_VALUE}`
useVehicle.stateChangeTypes = stateChangeTypes
export default useVehicle
